import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Save, Loader2, MapPin } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import PlacesAutocompleteInput from '../../components/ui/places-autocomplete-input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Dispatch Settings — company-wide defaults that act as fallbacks for
 * Build Day when a technician has no personal default of their own.
 *
 * Owner/admin only (backend enforces; UI also disables inputs for others).
 *
 * Fallback chain at Build Day open:
 *   tech.defaultStartAddress  → company.companyDefaultDepot     → ''
 *   tech.defaultMaxStops      → company.companyDefaultMaxStops  → 6
 */
const DispatchSettings = () => {
  const { profile } = useAuth();
  const { role } = useRole();
  const companyId = profile?.companyId;
  const isAdmin = ['owner', 'admin'].includes((role || '').toLowerCase());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [depot, setDepot] = useState('');
  const [maxStops, setMaxStops] = useState(6);
  const [initial, setInitial] = useState({ depot: '', maxStops: 6 });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const token = await getAuth().currentUser?.getIdToken();
        const res = await axios.get(`${API}/api/settings/dispatch`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const d = res.data || {};
        setDepot(d.companyDefaultDepot || '');
        setMaxStops(d.companyDefaultMaxStops ?? 6);
        setInitial({
          depot: d.companyDefaultDepot || '',
          maxStops: d.companyDefaultMaxStops ?? 6,
        });
      } catch (err) {
        console.error('[DispatchSettings] load failed:', err);
        toast.error('Could not load dispatch settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [companyId]);

  const dirty = (depot || '').trim() !== (initial.depot || '').trim()
             || Number(maxStops) !== Number(initial.maxStops);

  const handleSave = async () => {
    if (!isAdmin || saving || !dirty) return;
    setSaving(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const patch = {};
      if ((depot || '').trim() !== (initial.depot || '').trim()) {
        patch.companyDefaultDepot = (depot || '').trim();
      }
      if (Number(maxStops) !== Number(initial.maxStops)) {
        patch.companyDefaultMaxStops = Math.max(1, Math.min(20, parseInt(maxStops, 10) || 6));
      }
      const res = await axios.put(
        `${API}/api/settings/dispatch`,
        patch,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const after = res.data || {};
      setDepot(after.companyDefaultDepot || '');
      setMaxStops(after.companyDefaultMaxStops ?? 6);
      setInitial({
        depot: after.companyDefaultDepot || '',
        maxStops: after.companyDefaultMaxStops ?? 6,
      });
      toast.success('Dispatch defaults saved');
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Save failed';
      toast.error('Save failed', { description: detail });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="dispatch-settings-page">
      <header className="flex items-center gap-3">
        <Truck className="h-6 w-6 text-[var(--cb-orange)]" />
        <div>
          <h2 className="text-xl font-bold text-gray-900">Dispatch Defaults</h2>
          <p className="text-sm text-gray-500">
            Used by Build Day when a technician has no personal default.
            Per-technician overrides take priority over these values.
          </p>
        </div>
      </header>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="depot" className="text-sm font-medium text-gray-900">
                Company default depot
              </Label>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
                <PlacesAutocompleteInput
                  id="depot"
                  value={depot}
                  onChange={(v) => setDepot(v)}
                  placeholder="e.g., 100 Yard Rd, Springfield, IL"
                  disabled={!isAdmin || saving}
                  className="h-9 text-sm"
                  data-testid="company-depot-input"
                  maxLength={300}
                />
              </div>
              <p className="text-xs text-gray-500">
                Falls back to this address when a technician has no personal start
                address saved. Free-text; geocoded by Google Maps at Build Day time.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="max-stops" className="text-sm font-medium text-gray-900">
                Company default max stops
              </Label>
              <Input
                id="max-stops"
                type="number"
                min={1}
                max={20}
                value={maxStops}
                onChange={(e) => setMaxStops(e.target.value)}
                disabled={!isAdmin || saving}
                className="h-9 w-24 text-sm"
                data-testid="company-maxstops-input"
              />
              <p className="text-xs text-gray-500">
                Default capacity per technician per day. Range 1–20.
                Per-technician defaults override this value.
              </p>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                {dirty
                  ? 'Unsaved changes'
                  : 'All changes saved'}
              </span>
              <Button
                onClick={handleSave}
                disabled={!isAdmin || !dirty || saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="dispatch-settings-save-btn"
              >
                {saving ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</>
                ) : (
                  <><Save className="h-3.5 w-3.5 mr-1.5" /> Save</>
                )}
              </Button>
            </div>

            {!isAdmin && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Read-only — only owners and admins can change dispatch defaults.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default DispatchSettings;
